import React, { useEffect, useMemo } from "react";

import { Divider, Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import useCheckPermissions from "@fartherfinance/frontend/api/Authz/hooks/useCheckPermission";
import useFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";
import useGetResourcePermission from "@fartherfinance/frontend/api/Identity/hooks/useGetResourcePermission";
import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import {
  AdvisorId,
  partners,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import BaseDrawer from "../BaseDrawer/BaseDrawer";
import { TableContainerV2 } from "@src/multiCustodian/components/Client/Portfolio/shared/shared";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import { useAccessContext } from "./AccessContextProvider";
import LoadingStateTable from "./LoadinState";
import OrganizationAccordion from "./OrganizationAccordion";

const AccessTabWithBaseDrawer = () => {
  const { portfolioPlanId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const auth = useAdvisorRequestAuth();

  const portfolioPlanDetails = useGetPortfolioPlanById(
    portfolioPlanId,
    {},
    auth
  );

  const { data: resourceData } = useGetResourcePermission(
    {
      permission: "view",
      resourceType: "PortfolioPlan",
      resourceId: portfolioPlanId,
      query: {
        actorType: "Advisor",
      },
    },
    auth
  );

  const {
    color: { $surfaceLow, $border, $borderBold },
  } = useTheme();

  const { data: advisorsData } = useFartherEmployees("NoEng", auth);

  const { organizationsHash, advisorHash, resetState, onUpdate, isMutating } =
    useAccessContext();

  const bottomSave =
    Object.keys(organizationsHash).length > 0 ||
    Object.keys(advisorHash).length > 0;

  const extraHeight = bottomSave ? 2 * 72 : 72;

  // remove all not advisors
  const allAdvisors = useMemo(
    () =>
      (advisorsData?.advisors ?? [])
        .filter((a) => a.roles.includes("Engineering") === false)
        .filter((a) => a.roles.includes("Client Ops") === false)
        .filter((a) => a.roles.some((r) => r.includes("Advisor"))),
    [advisorsData?.advisors]
  );

  const advisorIds = allAdvisors.map((a) => a.advisorId);
  const totalAdvisors = allAdvisors.length;

  const advisorsSharedTo = (resourceData ?? []).filter((a) =>
    advisorIds.includes(a.personId as unknown as AdvisorId)
  );

  // if there is a personId of *, then it is public portfolio plan
  const isPublic = (resourceData ?? []).some((d) => d.personId === "*");

  // if public, all advisors are shared to it even if they are not in the list
  const totalAdvisorsSharedTo = isPublic
    ? totalAdvisors
    : advisorsSharedTo.length;

  const getNumberOfChanges = useMemo(() => {
    const organizationChanges = Object.values(organizationsHash).length;
    const advisorChanges = Object.keys(advisorHash).length;

    return organizationChanges + advisorChanges;
  }, [organizationsHash, advisorHash]);

  const isLoading =
    resourceData === undefined ||
    advisorsData === undefined ||
    portfolioPlanDetails.isLoading;

  const NonPrivatePortfolio =
    portfolioPlanDetails.data === undefined
      ? false
      : portfolioPlanDetails.data.data.plan.basePermission !== "Private";

  const permission =
    portfolioPlanDetails.data === undefined
      ? null
      : portfolioPlanDetails.data.data.plan.basePermission;

  const permissionText: null | string = useMemo(() => {
    if (permission === null) {
      return null;
    }

    switch (permission) {
      case "Private":
        return null;

      case "Public-Organization":
        return "This is not a private model portfolio and has fixed access for a specific organization";

      case "Public-Platform":
        return "This is not a private model portfolio and has fixed access for the entire platform";
    }
  }, [permission]);

  const canSeeAccessTab = useCheckPermissions(
    { type: "PortfolioPlan", id: portfolioPlanId, action: "view:sharing" },
    auth
  );

  useEffect(() => {
    // if not enough data to validate, return early
    if (canSeeAccessTab.data === undefined) {
      return;
    }

    if (canSeeAccessTab.data.authorized === false) {
      navigate({
        ...location,
        pathname: `/Advisor/Investments/ModelMarketplaceV2/${portfolioPlanId}/ModelDetails`,
      });
    }
  }, [canSeeAccessTab.data, location, navigate, portfolioPlanId]);

  const canUpdateAccessTabResult = useCheckPermissions(
    { type: "PortfolioPlan", id: portfolioPlanId, action: "update:sharing" },
    auth
  );

  const canUpdateAccessTab: boolean = useMemo(() => {
    if (canUpdateAccessTabResult.data === undefined) {
      return false;
    }

    return canUpdateAccessTabResult.data.authorized;
  }, [canUpdateAccessTabResult.data]);

  return (
    <BaseDrawer>
      {isLoading ? (
        <LoadingStateTable />
      ) : (
        <Stack direction={"column"} gap={2}>
          {NonPrivatePortfolio ? (
            <AlertInline
              message={
                permissionText ?? "This portfolio is a private portfolio model"
              }
              severity="info"
            />
          ) : null}

          <TableContainerV2
            style={{
              height: "632px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Stack
              flex={1}
              direction={"row"}
              justifyContent={"space-between"}
              p={"19px 16px"}
              style={{
                backgroundColor: $surfaceLow,
                borderBottom: `1px solid ${$border}`,
              }}
            >
              <Stack direction={"row"} flex={1} borderRadius={"5px"}>
                <Text variant="body" style={{ alignContent: "center" }}>
                  Organization and advisor access
                </Text>
              </Stack>

              <Stack
                direction={"row"}
                justifyContent={"normal"}
                border={`1px solid ${$border}`}
                borderRadius={"5px"}
                p={"6px 12px"}
                spacing={"12px"}
              >
                <Text variant="bodySmall">Total</Text>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  style={{ backgroundColor: $border, width: "1px" }}
                />
                <Text variant="bodySmall">{`${totalAdvisorsSharedTo}/${totalAdvisors} Advisors`}</Text>
              </Stack>
            </Stack>

            <Stack
              sx={{
                overflowY: "auto",
                height: `calc(100% - ${extraHeight}px)`, // footer and header take up the bottom space, so we need to subtract it from the height
              }}
            >
              {partners.map((organization) => (
                <OrganizationAccordion
                  key={organization}
                  organization={organization}
                />
              ))}
            </Stack>

            {bottomSave ? (
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                p={"19px 16px"}
                sx={{
                  backgroundColor: $border,
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  borderTop: `1px solid ${$borderBold}`,
                }}
              >
                {canUpdateAccessTab ? (
                  <Stack direction={"row"} spacing={2}>
                    <Text
                      variant="body"
                      style={{
                        alignContent: "center",
                      }}
                    >
                      {`${pluralize(
                        getNumberOfChanges,
                        "Unsaved change",
                        true
                      )}`}
                    </Text>
                  </Stack>
                ) : (
                  <Stack direction={"row"} spacing={2}>
                    <Text variant="body" style={{ alignContent: "center" }}>
                      You do not have permission to update sharing on this
                      portfolio model.
                    </Text>
                  </Stack>
                )}

                <Stack direction={"row"} spacing={2}>
                  <Button
                    isLoading={isMutating}
                    variant="text"
                    buttonType="primary"
                    text="Cancel"
                    onClick={resetState}
                  />
                  <Button
                    disabled={isMutating || canUpdateAccessTab === false}
                    variant="contained"
                    buttonType="primary"
                    text={"Save"}
                    onClick={onUpdate}
                  />
                </Stack>
              </Stack>
            ) : null}
          </TableContainerV2>
        </Stack>
      )}
    </BaseDrawer>
  );
};

export default AccessTabWithBaseDrawer;
